import { ApolloError } from '@apollo/client';

import { useErpCareerMembersQuery } from 'core/graphql';

import { useStableRefetch } from '../Groups/useStableRefetch';
import { ICareerMember } from './careerModel';

// Stable empty fallback: a fresh `[]` per render would hand react-table a new
// `data` reference every commit and re-run its internal effects.
const EMPTY_MEMBERS: ICareerMember[] = [];

interface ICareerMembersFilters {
  status?: string;
  directionId?: string;
  search?: string;
}

interface ICareerMembersHook {
  members: ICareerMember[];
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/**
 * Spec 0018 — the Участники tab (`careerMembers`). Flat list, no pagination:
 * the centre holds graduates in the low hundreds. Filter changes re-run the
 * query through Apollo's variable tracking; an empty filter is sent as
 * `undefined` so the backend applies no narrowing at all.
 */
export const useCareerMembers = (
  filters: ICareerMembersFilters = {},
): ICareerMembersHook => {
  const { data, loading, error, refetch } = useErpCareerMembersQuery({
    variables: {
      status: filters.status || undefined,
      directionId: filters.directionId || undefined,
      search: filters.search || undefined,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Pinned identity — this flows into the mutation hook whose callbacks land
  // in the react-table `columns` memo; an unstable one loops the render
  // ("Maximum update depth exceeded"). See `useStableRefetch`.
  const stableRefetch = useStableRefetch(refetch);

  return {
    members: data?.careerMembers ?? EMPTY_MEMBERS,
    loading,
    error,
    refetch: stableRefetch,
  };
};
